import React from 'react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import { formatCOP, formatPct } from '../utils/kpiFormatters';

interface DonutItem {
  name: string;
  value: number;
  pct: number;
  count?: number;
}

interface Props {
  data: DonutItem[];
  centerLabel?: string;
  totalFormatted?: string;
}

const COLORS = [
  '#059669', // Emerald
  '#0284C7', // Sky
  '#D97706', // Amber
  '#7C3AED', // Violet
  '#E11D48', // Rose
  '#475569', // Slate
  '#0D9488', // Teal
];

export const DonutChart: React.FC<Props> = ({
  data,
  centerLabel = 'Total',
  totalFormatted,
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex h-56 items-center justify-center text-xs text-text-muted">
        Sin datos registrados en el periodo
      </div>
    );
  }

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;
    const item = payload[0].payload as DonutItem;

    return (
      <div className="bg-neutral-900 text-white p-2.5 rounded-xl shadow-lg border border-neutral-800 text-xs">
        <p className="font-bold text-neutral-200">{item.name}</p>
        <p className="text-emerald-400 font-extrabold mt-0.5">{formatCOP(item.value)}</p>
        <p className="text-neutral-400 text-[11px] mt-0.5">
          {formatPct(item.pct, false)} del total {item.count !== undefined ? `(${item.count} órdenes)` : ''}
        </p>
      </div>
    );
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-2">
      {/* Gráfica de Dona */}
      <div className="relative w-48 h-48 shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Tooltip content={<CustomTooltip />} />
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              innerRadius={54}
              outerRadius={78}
              paddingAngle={3}
              stroke="none"
            >
              {data.map((_, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        {/* Texto al centro de la dona */}
        {totalFormatted && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-center px-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-text-muted">
              {centerLabel}
            </span>
            <span className="text-xs font-black text-text truncate max-w-[100px]">
              {totalFormatted}
            </span>
          </div>
        )}
      </div>

      {/* Leyenda Detallada */}
      <div className="flex-1 w-full space-y-2">
        {data.map((item, idx) => (
          <div key={item.name} className="flex items-center justify-between gap-2 text-xs">
            <div className="flex items-center gap-2 min-w-0">
              <span
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ backgroundColor: COLORS[idx % COLORS.length] }}
              />
              <span className="text-text font-medium truncate">{item.name}</span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-text-muted font-bold text-[11px]">
                {formatPct(item.pct, false)}
              </span>
              <span className="text-text font-semibold">
                {formatCOP(item.value)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
